import { useMemo, useContext, useCallback, Context } from "react"
import { filter } from "lodash"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createContentSetExposureMultiDateQuery } from "gatherers/multi-date/content-set-exposure"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import { computeFilteredSegment } from "helpers/segments"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { numberFormatter } from "helpers/formatters/numbers"
import { LOADING, OK } from "helpers/data-statuses"
import { ModelMetadata } from "resources/models"
import { ChartColors, GraphContextValue } from "connected-components/graph/types"
import { useContentSetContext, ContentSetMetadata } from "./context"
import { useActiveContentSet } from "./use-active-content-set"

interface ContentSetGraphProps {
  modelMetadata: ModelMetadata
  context: Context<GraphContextValue>
}

interface ContentSetItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: string
}

function createContentSetItems(
  contentSetMetadata: ContentSetMetadata | null,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): ContentSetItem[] {
  if (!contentSetMetadata?.factors) return []

  const factors = selectedCategory === "total"
    ? contentSetMetadata.factors
    : filter(contentSetMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: factor.name,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
  }))
}

export default function ContentSetGraph({ modelMetadata, context }: ContentSetGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
  } = useContext(context)

  const {
    selectedCategory,
    contentSetsMetadata,
    contentSetsStatus,
  } = useContentSetContext()

  const { selectedContentSetId, activeContentSet } = useActiveContentSet(context)

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter, normalization)
  const segment = useMemo(
    () => computeFilteredSegment(portfolioFilter, normalization),
    [portfolioFilter, normalization],
  )
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(
    () => createContentSetItems(activeContentSet, selectedCategory, disabledItems),
    [activeContentSet, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: ContentSetItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => {
    if (!selectedContentSetId) return []
    // TODO: [INTRADAY-BATCH] replace with intraday batch data when isIntradayEnabled
    return [
      createContentSetExposureMultiDateQuery(
        modelMetadata.id,
        selectedContentSetId,
        instrumentWithSegment,
        baseWithSegment,
        startDate,
        endDate,
      ),
    ]
  }, [modelMetadata, selectedContentSetId, instrumentWithSegment, baseWithSegment, startDate, endDate])

  if (contentSetsStatus === LOADING) {
    return <MultiLineGraph
      status={LOADING}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage="Loading content sets..."
    />
  }

  if (!contentSetsMetadata?.length) {
    return <MultiLineGraph
      status={OK}
      data={[]}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage=""
      emptyMessage="No content sets are configured for this account."
    />
  }

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      formatter: numberFormatter,
      loadingMessage: "Loading content set data...",
      errorMessage: "Failed to load content set data. Please try again.",
      emptyMessage: "No content set data available for the selected period.",
    }}
  />
}
